import { FormTypes } from '@oneblink/types'
import { submissionService } from '@oneblink/sdk-core'
import { v4 as uuidv4 } from 'uuid'

import makeIsShowing from '../../is-element-showing'
import { getElementSubmissionValue } from '@oneblink/sdk-core/dist/replaceCustomValues'

const replaceInjectablesFormatters = {
  formatDate: (value: string) => new Date(value).toDateString(),
  formatTime: (value: string) => new Date(value).toTimeString(),
  formatDateTime: (value: string) => new Date(value).toString(),
  formatNumber: (value: number) => Number(value).toString(),
  formatCurrency: (value: number) => Number(value).toFixed(2),
}

const replaceInjectablesContext = {
  userProfile: undefined,
  task: undefined,
  taskGroup: undefined,
  taskGroupInstance: undefined,
}

function parse<SubmissionType extends Record<PropertyKey, unknown>>(
  str: string,
  submission: SubmissionType,
  definition: FormTypes.Form,
  elementNamesSet: Set<string>,
) {
  const isElementVisible = makeIsShowing(submission, definition.elements)

  const matchesIterator = str.matchAll(/{ELEMENT:(.*?)}/g)
  const regexMatches = [...matchesIterator]
  const elementNames = regexMatches.map((regexMatch) => regexMatch?.[1])

  // if no element names found then its just a string so return it
  if (!elementNames?.length) {
    return str
  }

  elementNames.forEach((name) => {
    if (!elementNamesSet.has(name)) {
      console.warn(
        `While parsing custom label/value "${str}" could not find element with name: ${name}`,
      )
    }
  })

  // if there are element names but some are not visible (or do not exist),
  // then we shouldnt be displaying anything
  if (!elementNames?.every(isElementVisible)) {
    return
  }

  let result = str
  for (const elementName of elementNames) {
    const elementAndValue = getElementSubmissionValue({
      propertyName: elementName,
      formElements: definition.elements,
      submission,
      ...replaceInjectablesFormatters,
      ...replaceInjectablesContext,
    })
    if (typeof elementAndValue?.value !== 'string') {
      return
    }
    result = result.replace(
      `{ELEMENT:${elementName}}`,
      elementAndValue.value ?? '',
    )
  }

  return result.trim()
}

export function populateValue<
  SubmissionType extends Record<PropertyKey, unknown>,
>({
  targetElement,
  sourceMeta,
  submission,
  definition,
}: {
  targetElement: FormTypes.FormElement
  sourceMeta: PopulateValueMeta
  submission: SubmissionType
  definition: FormTypes.Form
}) {
  if (!('name' in targetElement)) {
    console.info({ targetElement })
    throw new Error(`Element ${targetElement.id} is not of the right type!`)
  }

  const isElementVisible = makeIsShowing(submission, definition.elements)
  const { sourceElements, condition } = sourceMeta
  const { predicateElement, value } = condition
  if (
    !isElementVisible(predicateElement) ||
    !sourceElements.some(isElementVisible)
  ) {
    return
  }

  const predicateValue = submissionService.getElementSubmissionValue({
    propertyName: predicateElement,
    formElements: definition.elements,
    submission,
    ...replaceInjectablesFormatters,
    ...replaceInjectablesContext,
  })

  if (predicateValue?.value !== value) {
    return
  }

  const sourceValue = sourceElements
    .reduce((memo: string[], sourceElementName) => {
      const v = submissionService.getElementSubmissionValue({
        propertyName: sourceElementName,
        formElements: definition.elements,
        submission,
        ...replaceInjectablesFormatters,
        ...replaceInjectablesContext,
      })?.value as string

      memo.push(v)
      return memo
    }, [])
    .join(' ')

  // @ts-ignore
  submission[targetElement.name] = sourceValue.length ? sourceValue : undefined
}

export function makeOption<
  SubmissionType extends Record<PropertyKey, unknown>,
>({
  targetElement,
  sourceMeta,
  submission,
  definition,
  elementNamesSet,
}: {
  targetElement: FormTypes.FormElement
  sourceMeta: PopulateOptionSetMeta['dynamicOptions'][number]
  submission: SubmissionType
  definition: FormTypes.Form
  elementNamesSet: Set<string>
}) {
  if (!('name' in targetElement)) {
    console.info({ targetElement })
    throw new Error(`Element ${targetElement.id} is not of the right type!`)
  }

  const label = parse(sourceMeta.label, submission, definition, elementNamesSet)
  const value = parse(sourceMeta.value, submission, definition, elementNamesSet)

  if (!label || !value) {
    return
  }
  return {
    id: uuidv4(),
    label,
    value,
  }
}

export function getOptions<
  SubmissionType extends Record<PropertyKey, unknown>,
>({
  targetElement,
  optionsMeta,
  submission,
  definition,
  elementNamesSet,
}: {
  targetElement: FormTypes.FormElement
  optionsMeta: PopulateOptionSetMeta['dynamicOptions']
  submission: SubmissionType
  definition: FormTypes.Form
  elementNamesSet: Set<string>
}) {
  const options = optionsMeta.reduce(
    (memo: FormTypes.ChoiceElementOption[], sourceMeta) => {
      const option = makeOption({
        targetElement,
        sourceMeta,
        submission,
        definition,
        elementNamesSet,
      })

      if (option) {
        memo.push(option)
      }

      return memo
    },
    [],
  )

  return options
}

export function conditionallyReadOnly<
  SubmissionType extends Record<PropertyKey, unknown>,
>({
  targetElement,
  meta,
  submission,
  definition,
}: {
  targetElement: FormTypes.FormElement
  meta: ConditionallyReadOnlyMeta
  submission: SubmissionType
  definition: FormTypes.Form
}): FormTypes.FormElement & FormTypes.FormElementReadOnly {
  if (!('name' in targetElement)) {
    console.info({ targetElement })
    throw new Error(`Element ${targetElement.id} is not of the right type!`)
  }
  const isElementVisible = makeIsShowing(submission, definition.elements)
  const { predicateElement, value } = meta.condition
  if (!isElementVisible(predicateElement)) {
    return {
      ...targetElement,
      readOnly: false,
    }
  }

  const predicate = submissionService.getElementSubmissionValue({
    propertyName: predicateElement,
    formElements: definition.elements,
    submission,
    ...replaceInjectablesFormatters,
    ...replaceInjectablesContext,
  })

  if (predicate?.value !== value) {
    return {
      ...targetElement,
      readOnly: false,
    }
  }

  return {
    ...targetElement,
    readOnly: true,
  }
}
